import { api } from './api';

/**
 * Scadenziario contabile — tipi, fetcher e logica condivisi.
 *
 * Estratto da InvoicesPage per essere riusato dallo stream "Oggi"
 * (useActionStream): stessa fonte dati e stessa definizione di "evento
 * atteso", così il numero promesso dal badge della pagina Contabilità
 * e quello dello stream combaciano sempre.
 */

export interface InvoiceItem {
  id: string;
  number: string | null;
  type: 'FATTURA' | 'PROFORMA' | 'NOTA_CREDITO' | 'AVVISO_PAGAMENTO';
  client: { id: string; legalName: string; shortName: string };
  project: { id: string; code: string; name: string } | null;
  issueDate: string | null;
  dueDate: string | null;
  subtotal: string; vat: string; total: string;
  status: string;
  ficDocumentId: number | null;
  ficUrl: string | null;
}

export interface QuoteItem {
  id: string;
  number: string | null;
  title: string | null;
  client: { id: string; legalName: string; shortName: string };
  project: { id: string; code: string; name: string } | null;
  status: string;
  issueDate: string | null;
  validUntil: string | null;
  subtotal: string; vat: string; total: string;
  sentAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
  convertedToInvoice: { id: string; number: string | null } | null;
}

export type DocKind = 'quote' | 'invoice';

export interface UnifiedRow {
  kind: DocKind;
  invoiceType: InvoiceItem['type'] | null;
  id: string;
  number: string | null;
  subtitle: string;
  client: { id: string; legalName: string };
  project: { id: string; code: string; name: string } | null;
  primaryDate: string | null;
  secondaryDate: string | null;
  total: string;
  status: string;
  raw: QuoteItem | InvoiceItem;
}

// Query condivise — STESSE queryKey/fetch dello scadenziario e dello stream
// "Oggi": chi le usa partecipa alla stessa cache TanStack Query (un solo fetch).
//
// ⚠️ Prendono UNA pagina da 200: bastano allo scadenziario (eventi dei
// prossimi 60 giorni) ma NON sono una lista completa — in DB ci sono già 594
// fatture. Per una lista di documenti usa `documentsQuery` qui sotto, che
// pagina e filtra sul server.
export const invoicesQueryOptions = {
  queryKey: ['invoices'] as const,
  queryFn: () => api.get<{ items: InvoiceItem[] }>('/invoices?pageSize=200'),
};

export const quotesQueryOptions = {
  queryKey: ['quotes'] as const,
  queryFn: () => api.get<{ items: QuoteItem[] }>('/quotes?pageSize=200'),
};

// ── Lista documenti paginata (tab Documenti dell'Amministrazione) ───────────

interface Paginated<T> { items: T[]; page: number; pageSize: number; total: number; totalPages: number; }

/**
 * Fonte della lista: i preventivi vivono su /quotes, tutto il resto su
 * /invoices, dove `type` sceglie fattura / nota di credito / proforma /
 * avviso. 'all' = tutti i documenti fiscali (nessun filtro di tipo).
 */
export type DocSource = 'all' | 'quote' | InvoiceItem['type'];

export interface DocumentsParams {
  source: DocSource;
  /** Stato esatto dell'enum (InvoiceStatus | QuoteStatus); '' = tutti. */
  status: string;
  search: string;
  /** Interruttore "Anni scorsi": spegne la data di taglio dell'Amministrazione. */
  includeHistory?: boolean;
  page: number;
  pageSize: number;
}

export interface DocumentsPage {
  rows: UnifiedRow[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/**
 * Una pagina di documenti, filtrata e contata DAL SERVER: il numero mostrato
 * in filterbar e nella paginazione è quello vero, non quello del cap.
 *
 * La queryKey parte da 'invoices'/'quotes' apposta: le mutation di
 * `useDocActions` invalidano quei prefissi e rinfrescano anche questa lista.
 */
export function documentsQuery(p: DocumentsParams) {
  const quotes = p.source === 'quote';
  const sp = new URLSearchParams();
  sp.set('page', String(p.page));
  sp.set('pageSize', String(p.pageSize));
  if (p.status) sp.set('status', p.status);
  if (p.search.trim()) sp.set('search', p.search.trim());
  if (p.includeHistory) sp.set('includeHistory', 'true');
  if (!quotes && p.source !== 'all') sp.set('type', p.source);
  return {
    queryKey: [quotes ? 'quotes' : 'invoices', 'list', p] as const,
    queryFn: async (): Promise<DocumentsPage> => {
      const r = await api.get<Paginated<any>>(`${quotes ? '/quotes' : '/invoices'}?${sp.toString()}`);
      const items = r.items ?? [];
      return {
        rows: quotes ? toUnifiedRows(items as QuoteItem[], []) : toUnifiedRows([], items as InvoiceItem[]),
        page: r.page,
        pageSize: r.pageSize,
        total: r.total,
        totalPages: r.totalPages,
      };
    },
    staleTime: 15_000,
  };
}

/** Mappa preventivi + fatture nel modello riga unificato, ordinato per emissione desc. */
export function toUnifiedRows(quotes: QuoteItem[], invoices: InvoiceItem[]): UnifiedRow[] {
  const qs: UnifiedRow[] = quotes.map((q) => ({
    kind: 'quote' as const,
    invoiceType: null,
    id: q.id,
    number: q.number,
    subtitle: q.title ?? 'Proposta commerciale',
    client: q.client,
    project: q.project,
    primaryDate: q.issueDate,
    secondaryDate: q.validUntil,
    total: q.total,
    status: q.status,
    raw: q,
  }));
  const ivs: UnifiedRow[] = invoices.map((i) => ({
    kind: 'invoice' as const,
    invoiceType: i.type,
    id: i.id,
    number: i.number,
    subtitle: i.type.replace(/_/g, ' '),
    client: i.client,
    project: i.project,
    primaryDate: i.issueDate,
    secondaryDate: i.dueDate,
    total: i.total,
    status: i.status,
    raw: i,
  }));
  return [...qs, ...ivs].sort((a, b) => {
    const da = a.primaryDate ? new Date(a.primaryDate).getTime() : 0;
    const db = b.primaryDate ? new Date(b.primaryDate).getTime() : 0;
    return db - da;
  });
}

// Scadenziario: eventi attesi (incassi + firme) entro N giorni.
export const SCADENZIARIO_DAYS = 60;
export function computeUpcoming(rows: UnifiedRow[]): UnifiedRow[] {
  const cutoff = Date.now() + SCADENZIARIO_DAYS * 86400000;
  return rows
    .filter((r) => {
      const open = r.kind === 'invoice'
        ? ['EMESSA', 'INVIATA'].includes(r.status)
        : ['INVIATO', 'VISUALIZZATO'].includes(r.status);
      if (!open) return false;
      const d = r.secondaryDate ? new Date(r.secondaryDate).getTime() : null;
      return !d || d <= cutoff;
    })
    .sort((a, b) => {
      const da = a.secondaryDate ? new Date(a.secondaryDate).getTime() : 0;
      const db = b.secondaryDate ? new Date(b.secondaryDate).getTime() : 0;
      return da - db;
    });
}
